import { DEFAULT_PLANNED_SAMPLE_SIZE } from "@digital-twins/shared-types";
import type { AudienceSelectionRef } from "@digital-twins/shared-types";
import {
  getQuestionnaireDraftApi,
  updateQuestionnaireDraftApi,
} from "@/features/studies/questionnaire/api/questionnaireDraftsApi";
import { isQuestionnaireDraftConflict } from "@/features/studies/questionnaire/utils/compileErrorMessage";

const PENDING_POPULATION_ID = "population_pending_selection";

/**
 * One in-flight save per draft. Review saves overlap: a debounce that has
 * already started reading cannot be cancelled, and the calls finish in either
 * order. Later calls wait on this chain and only the newest requested size
 * is written, so an older save that wins the revision first cannot leave its
 * value in place.
 */
interface SampleSizeSaveLane {
  latest: number;
  tail: Promise<void>;
}

const sampleSizeSaveLanes = new Map<string, SampleSizeSaveLane>();

/** Drops queued saves. Tests call this so one case cannot block the next. */
export function resetPlannedSampleSizeSavesForTests(): void {
  sampleSizeSaveLanes.clear();
}

/**
 * Writes stay off until every serving Nest pod accepts `plannedSampleSize`.
 * A pod that stores the field makes an older pod reject the draft in its
 * closed audience validator, before the Nest/Python digest check. Turn this
 * on, together with `DT_PLANNED_SAMPLE_SIZE_PERSISTENCE` on the backend, only
 * after that rollout. Unset is off.
 */
export function isPlannedSampleSizePersistenceEnabled(): boolean {
  return import.meta.env.DT_VITE_PLANNED_SAMPLE_SIZE_PERSISTENCE === "true";
}

function isLatestRequestedSampleSize(
  lane: SampleSizeSaveLane,
  sampleSize: number,
): boolean {
  return lane.latest === sampleSize;
}

function storedPlannedSampleSize(audience: AudienceSelectionRef): number | null {
  return typeof audience.plannedSampleSize === "number"
    ? audience.plannedSampleSize
    : null;
}

function hasRealAudience(
  audience: AudienceSelectionRef | undefined,
): audience is AudienceSelectionRef {
  return (
    typeof audience?.populationId === "string" &&
    audience.populationId.length > 0 &&
    audience.populationId !== PENDING_POPULATION_ID
  );
}

/**
 * Store the planned sample size on the draft, beside the audience pool.
 *
 * The pool stays on `estimatedRespondentCount`. This writes only
 * `plannedSampleSize`, keeping every other audience field from the draft
 * just read. A draft that has no stored size and is still on the product
 * default is left untouched: resume already restores that default, and a
 * write would revise the questionnaire for no change in what launch runs.
 *
 * Calls for one draft run one at a time. A newer size replaces the older
 * one in the lane, and the older call skips its own write when it has not
 * sent yet. When the older write is already in flight, it is left to finish
 * and the newer call then stores its size, including when that older write
 * is what moved the revision.
 *
 * A compare-and-swap conflict is not written again. The draft revision does
 * not say which field moved, and a matching `plannedSampleSize` does not
 * prove the conflict left that field alone: another tab can change 70 to 90
 * and back to 70 while this PATCH is in flight. The refreshed read is
 * adopted when it is already this call's size. Otherwise the retry stops
 * and the other tab's revision stays.
 */
export async function persistPlannedSampleSize(
  draftId: string,
  sampleSize: number,
): Promise<boolean> {
  if (!isPlannedSampleSizePersistenceEnabled()) {
    return true;
  }

  const lane = sampleSizeSaveLanes.get(draftId) ?? {
    latest: sampleSize,
    tail: Promise.resolve(),
  };
  lane.latest = sampleSize;
  sampleSizeSaveLanes.set(draftId, lane);

  const run = lane.tail.then(() =>
    writePlannedSampleSize(draftId, sampleSize, lane),
  );
  lane.tail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

async function writePlannedSampleSize(
  draftId: string,
  sampleSize: number,
  lane: SampleSizeSaveLane,
): Promise<boolean> {
  if (!isLatestRequestedSampleSize(lane, sampleSize)) {
    return false;
  }

  const current = await getQuestionnaireDraftApi(draftId);
  if (!isLatestRequestedSampleSize(lane, sampleSize)) {
    return false;
  }

  const audience = current.artifacts?.draft?.audience;
  if (!hasRealAudience(audience)) {
    return false;
  }

  const serverPlannedSampleSize = storedPlannedSampleSize(audience);
  if (serverPlannedSampleSize === sampleSize) {
    return true;
  }
  if (
    serverPlannedSampleSize == null &&
    sampleSize === DEFAULT_PLANNED_SAMPLE_SIZE
  ) {
    return true;
  }

  try {
    await updateQuestionnaireDraftApi(draftId, {
      draft: {
        audience: { ...audience, plannedSampleSize: sampleSize },
      },
      ...(typeof current.revision === "number"
        ? { expectedRevision: current.revision }
        : {}),
    });
    return true;
  } catch (error) {
    if (!isQuestionnaireDraftConflict(error)) {
      throw error;
    }
  }

  if (!isLatestRequestedSampleSize(lane, sampleSize)) {
    return false;
  }

  const refreshed = await getQuestionnaireDraftApi(draftId);
  if (!isLatestRequestedSampleSize(lane, sampleSize)) {
    return false;
  }
  const refreshedAudience = refreshed.artifacts?.draft?.audience;
  if (!hasRealAudience(refreshedAudience)) {
    return false;
  }
  return storedPlannedSampleSize(refreshedAudience) === sampleSize;
}
